import * as React from 'react';
import { useRouter } from 'next/router';
import useViewer from 'hooks/useViewer';
import Head from 'components/Head';
import {
  useGetFreeOptionFlowLazyQuery,
  useGetOptionFlowLazyQuery,
  useGetUserForDashboardLazyQuery,
  useHasNewOptionFlowQuery,
  useHasNewFreeOptionFlowQuery,
} from 'types/generated/client';
import { AuthStatus } from 'constants/auth';
import { isMarketHours } from 'utils/shared/time/marketHours';
import { hasDashboardAccess, hasCompletedOpraAgreement } from 'services/client/token';
import {
  USE_TIMESTAMP_POLLING,
  getInitialPollingTimestamp,
  HYDRATE_FLOW_SIDES_BEFORE_FETCH,
  REALTIME_FLOW_ENABLED,
  FULL_FEED_TOGGLE_ENABLED,
} from 'constants/flags';
import { REALTIME_URL } from 'constants/api';
import { useOptionFlowRealtime } from './useOptionFlowRealtime';
import { isAwaitingDelayedFlow } from './isAwaitingDelayedFlow';
import { isRealtimeFlowEnabled } from './realtimeFlowGate';
import { dailyVolumeFromTickers, filterBySides } from 'utils/shared/trades/realtimeFlowAdapter';
import { ELECTRONIC_AGREEMENT_PAGE, OPTION_FLOW_PAGE, PageIcons } from 'constants/pages';
import FlowPageContainer from 'layouts/FlowPageContainer';
import OptionFlowTable from 'components/OptionFlowTable';
import { GetOptionFlowQuery } from 'types/generated/client';
import { getIsNativePlatform } from 'utils/client/mobile';
import useInlineScript from 'hooks/useInlineScript';
import { PRODUCT_TOUR_SCRIPT } from 'utils/client/productTour';
import { useApiGateway } from 'hooks/useApi';
import FloatingBox from 'components/auth/FloatingBox';
import {
  areTradeSidesEqual,
  getTradeSidesForFlowSideBasis,
} from 'utils/shared/trades/flowSideBasis';
import {
  getDefaultFlowSideBasisSelection,
  normalizeFlowSideBasisSelection,
  FlowSideBasis,
  FlowSideBasisSelection,
} from 'utils/shared/trades/presets';
import { getSavedFlowSideSelection } from 'utils/shared/trades/flowSideHydration';
// import TopBanner from 'components/TopBanner/Banner';

const POLL_INTERVAL = 10000;
const TITLE = 'Live Options Flow and Unusual Options Activity';
const DESCRIPTION =
  'The #1 tool to detect unusual options activity using the best options flow tool - access for free. InsiderFinance is an award-nominated realtime order flow dashboard. Smart money real-time option flow and dark pool prints.';
const DEFAULT_PAID_FLOW_SIDE_BASIS = getDefaultFlowSideBasisSelection();
const DEFAULT_PAID_TRADE_SIDES = getTradeSidesForFlowSideBasis(DEFAULT_PAID_FLOW_SIDE_BASIS);

const flowSideBasisToCountQuery = (basis: FlowSideBasis[]) => `?flowSideBasis=${basis.join(',')}`;
// @ts-ignore
const INJECTED_PAGE_DATA = typeof window !== 'undefined' ? window?.injectedPageData : undefined;

const Placeholder = () => {
  return (
    <>
      <Head title={TITLE} description={DESCRIPTION} useCurrentUrl />

      <div></div>
    </>
  );
};

export default function OptionFlowPageContainer() {
  const viewer = useViewer();
  const router = useRouter();
  const [
    getFreeOptionFlowQuery,
    { data: dataFree, called: calledFree, loading: isLoadingFree, refetch: refetchFree },
  ] = useGetFreeOptionFlowLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [
    getOptionFlowQuery,
    { data: dataPaid, called: calledPaid, loading: isLoadingPaid, refetch: refetchPaid },
  ] = useGetOptionFlowLazyQuery({
    fetchPolicy: 'no-cache',
  });
  // Separate feed for the command center "live count" preview — never drives the table.
  const [getPreviewOptionFlowQuery, { data: previewData, loading: isPreviewLoading }] =
    useGetOptionFlowLazyQuery({
      fetchPolicy: 'no-cache',
    });
  const [getUserQuery, { data: userData, called: userCalled, loading: userLoading }] =
    useGetUserForDashboardLazyQuery();
  const [injectedPageData, setInjectedPageData] = React.useState<GetOptionFlowQuery | undefined>(
    undefined,
  );
  // keepDataOnRefetch: these counts poll the same endpoint every POLL_INTERVAL.
  // Without it, each refetch blanks `data` to null mid-flight, which collapses the
  // derived counts (and the "hidden trades" badge) to 0 and back on every poll.
  const { get: getTotalFlowCount, data: totalFlowCountData } = useApiGateway(
    '/v1/products/option-flow/count',
    { keepDataOnRefetch: true },
  );
  const totalFlowCount = totalFlowCountData?.count ?? undefined;
  const { get: getPaidFlowCount, data: paidFlowCountData } = useApiGateway(
    '/v1/products/option-flow/count',
    { keepDataOnRefetch: true },
  );
  const paidFlowCount = paidFlowCountData?.count ?? null;
  const { get: getFreeFlowCount, data: freeFlowCountData } = useApiGateway(
    '/v1/products/option-flow-free/count',
    { keepDataOnRefetch: true },
  );
  const freeFlowCount = freeFlowCountData?.count ?? null;
  const [paidTradeSides, setPaidTradeSides] = React.useState(DEFAULT_PAID_TRADE_SIDES);
  const [paidFlowSideBasis, setPaidFlowSideBasis] = React.useState<FlowSideBasis[]>(
    DEFAULT_PAID_FLOW_SIDE_BASIS,
  );

  // The user's saved filters are resolved once the query has been called and is
  // no longer in flight (data present, or definitively errored/empty).
  const settingsResolved = userCalled && !userLoading;
  const [hasSeededFlowSides, setHasSeededFlowSides] = React.useState(false);
  const hasSeededFlowSidesRef = React.useRef(false);

  // Seed the committed paid trade sides + side basis from the user's saved filters
  // before the first fetch, so the paid feed doesn't query the default side and
  // then have to correct it. See HYDRATE_FLOW_SIDES_BEFORE_FETCH for the strategy.
  React.useEffect(() => {
    if (hasSeededFlowSidesRef.current || !settingsResolved) {
      return;
    }
    hasSeededFlowSidesRef.current = true;
    const { flowSideBasis: savedFlowSideBasis, tradeSides: savedTradeSides } =
      getSavedFlowSideSelection(userData?.users_by_pk?.optionFlowFilters);
    setPaidTradeSides((current) =>
      areTradeSidesEqual(current, savedTradeSides) ? current : savedTradeSides,
    );
    setPaidFlowSideBasis(savedFlowSideBasis);
    setHasSeededFlowSides(true);
  }, [settingsResolved, userData]);

  // When hydrating before fetch, hold the initial paid fetch until the saved sides
  // are seeded; in eager mode fetch immediately and let the seed correct it.
  const canFetchPaidFlow = HYDRATE_FLOW_SIDES_BEFORE_FETCH ? hasSeededFlowSides : true;

  // Track previous count values to detect genuine changes (not temporary resets)
  const prevPaidFlowCount = React.useRef<number | null>(null);
  const prevFreeFlowCount = React.useRef<number | null>(null);

  // Initialize with 24 hours ago to ensure initial data load
  const [lastSeenPaidTimestamp, setLastSeenPaidTimestamp] = React.useState<string>(
    getInitialPollingTimestamp,
  );
  const [lastSeenFreeTimestamp, setLastSeenFreeTimestamp] = React.useState<string>(
    getInitialPollingTimestamp,
  );

  const isLoadingAuth = viewer.status === AuthStatus.Loading;
  const shouldHaveFree =
    viewer.status === AuthStatus.Anonymous ||
    (viewer.status === AuthStatus.User && !hasDashboardAccess(viewer));
  const shouldHavePaid = viewer.status === AuthStatus.User && hasDashboardAccess(viewer);
  const isInitialFlowCalled = !isLoadingAuth && shouldHavePaid ? calledPaid : calledFree;
  const paidOptionFlowData = shouldHavePaid ? dataPaid : undefined;
  const freeOptionFlowData = shouldHaveFree ? dataFree : undefined;

  // Realtime (Phase 3): while the flow socket is live, drop the count polls +
  // full refetches; render pushed rows through the adapter. Side toggles become
  // a client-side filter (D2). Server enforces the free ASK+delay policy, so the
  // same code path serves free/anon. On fallback/off, the polling paths re-arm.
  // Injected email pages always use the legacy path (see isRealtimeFlowEnabled).
  const realtimeFlowEnabled = isRealtimeFlowEnabled({
    flagEnabled: REALTIME_FLOW_ENABLED,
    hasRealtimeUrl: !!REALTIME_URL,
    hasInjectedPageData: !!INJECTED_PAGE_DATA,
    isLoadingAuth,
    hasViewerTier: shouldHavePaid || shouldHaveFree,
  });
  // Baseline ("full") feed toggle. Shown to everyone (incl. free/anon) once flagged on and the
  // realtime flow feed is active — free users see it locked as an upsell. Only paid users can
  // actually switch it on (canUseBaseline); the server also enforces paid-only. When on, the
  // hook re-subscribes to the full firehose server-side.
  const showBaselineToggle = FULL_FEED_TOGGLE_ENABLED && realtimeFlowEnabled;
  const canUseBaseline = showBaselineToggle && shouldHavePaid;
  const [showBaseline, setShowBaseline] = React.useState(false);
  const feedClass = canUseBaseline && showBaseline ? 'full' : 'smart';
  const {
    rows: realtimeRows,
    tickers: realtimeTickers,
    status: realtimeStatus,
    tier: realtimeTier,
    sessionDate: realtimeSessionDate,
  } = useOptionFlowRealtime(realtimeFlowEnabled, feedClass);
  // Sticky once live: keep rendering realtime rows through a reconnect instead of
  // reverting to stale Apollo data + a count/refetch burst (§17.2 P1).
  const usingRealtime =
    realtimeFlowEnabled &&
    (realtimeStatus === 'live' || (realtimeStatus === 'connecting' && realtimeRows.length > 0));
  const shouldUseLegacyFlow = !realtimeFlowEnabled || realtimeStatus === 'fallback';
  const awaitingDelayedFlow = isAwaitingDelayedFlow({
    shouldHaveFree,
    hasInjectedPageData: !!injectedPageData,
    usingRealtime,
    realtimeTier,
    realtimeRowCount: realtimeRows.length,
  });
  // Stable while nothing changes, so the memoized market table does not re-render on every flush.
  const delayedFlowWait = React.useMemo(
    () => (awaitingDelayedFlow ? { sessionDate: realtimeSessionDate } : null),
    [awaitingDelayedFlow, realtimeSessionDate],
  );

  const realtimeAdapted = React.useMemo(() => {
    if (!usingRealtime) return undefined;
    // Only apply the paid side filter when the server actually granted paid.
    const activeSides = shouldHavePaid && realtimeTier !== 'free' ? paidTradeSides : null;
    // `flow` stays raw FlowRows (filterBySides returns references — no per-row object copy).
    // The ~17 ticker fields are joined per-row downstream in FlowPageContainer's single derive
    // pass (via the `tickerInfo` prop) instead of being materialized into a second full array
    // here. dailyVolume is ticker-level (one entry per ticker), so it's derived straight from
    // the tickers map without touching the trade rows.
    const flow = filterBySides(realtimeRows, activeSides);
    return { flow, dailyVolume: dailyVolumeFromTickers(realtimeTickers) };
  }, [usingRealtime, shouldHavePaid, realtimeTier, paidTradeSides, realtimeRows, realtimeTickers]);

  // New timestamp-based polling (feature flagged)
  const {
    data: paidLatestTimestampData,
    startPolling: startPaidTimestampPolling,
    stopPolling: stopPaidTimestampPolling,
  } = useHasNewOptionFlowQuery({
    variables: { since: lastSeenPaidTimestamp, tradeSides: paidTradeSides },
    skip: !USE_TIMESTAMP_POLLING || !shouldHavePaid || !shouldUseLegacyFlow,
  });

  const {
    data: freeLatestTimestampData,
    startPolling: startFreeTimestampPolling,
    stopPolling: stopFreeTimestampPolling,
  } = useHasNewFreeOptionFlowQuery({
    variables: { since: lastSeenFreeTimestamp },
    skip: !USE_TIMESTAMP_POLLING || !shouldHaveFree || !shouldUseLegacyFlow,
  });

  React.useEffect(() => {
    if (INJECTED_PAGE_DATA) {
      setInjectedPageData(INJECTED_PAGE_DATA);
    }
  }, []);

  if (!INJECTED_PAGE_DATA && !getIsNativePlatform()) {
    // NOTE: This needs to be hoisted and in context or something to be only called once.
    // Or does the useEffect in the hook handle it with no dependencies?
    useInlineScript({
      body: PRODUCT_TOUR_SCRIPT,
    });
  }

  React.useEffect(() => {
    if (
      isLoadingAuth ||
      !shouldUseLegacyFlow ||
      !shouldHavePaid ||
      calledPaid ||
      !canFetchPaidFlow
    ) {
      return;
    }

    getOptionFlowQuery({ variables: { tradeSides: paidTradeSides } });
  }, [
    calledPaid,
    canFetchPaidFlow,
    getOptionFlowQuery,
    isLoadingAuth,
    paidTradeSides,
    shouldUseLegacyFlow,
    shouldHavePaid,
  ]);

  React.useEffect(() => {
    if (USE_TIMESTAMP_POLLING) return; // Exit if timestamp polling is enabled

    let interval: number | undefined;

    if (shouldHavePaid && canFetchPaidFlow && shouldUseLegacyFlow) {
      const countRequest = { endpoint: flowSideBasisToCountQuery(paidFlowSideBasis) };
      getPaidFlowCount(countRequest);

      // @ts-ignore issue with Node / browser types
      interval = setInterval(() => {
        if (isMarketHours()) {
          getPaidFlowCount(countRequest);
        }
      }, POLL_INTERVAL);
    }

    return () => clearInterval(interval);
  }, [shouldHavePaid, canFetchPaidFlow, paidFlowSideBasis, shouldUseLegacyFlow]);

  React.useEffect(() => {
    if (USE_TIMESTAMP_POLLING) return; // Exit if timestamp polling is enabled

    let interval: number | undefined;

    if (shouldHaveFree && shouldUseLegacyFlow) {
      getFreeFlowCount();

      // @ts-ignore issue with Node / browser types
      interval = setInterval(() => {
        if (isMarketHours()) {
          getFreeFlowCount();
        }
      }, POLL_INTERVAL);
    }

    return () => clearInterval(interval);
  }, [shouldHaveFree, shouldUseLegacyFlow]);

  // The "hidden trades" upsell count is legacy-polling data too. Keep it off while
  // realtime owns /flow so the flag fully disables the old count polling path.
  React.useEffect(() => {
    if (USE_TIMESTAMP_POLLING) return;
    if (!shouldUseLegacyFlow) return;
    if (!shouldHaveFree) return;
    getTotalFlowCount();
    // @ts-ignore issue with Node / browser types
    const interval: number = setInterval(() => {
      if (isMarketHours()) getTotalFlowCount();
    }, 2 * 60_000);
    return () => clearInterval(interval);
  }, [shouldHaveFree, shouldUseLegacyFlow]);

  React.useEffect(() => {
    if (isLoadingAuth || !shouldUseLegacyFlow || !shouldHaveFree || calledFree) {
      return;
    }

    getFreeOptionFlowQuery();
  }, [calledFree, getFreeOptionFlowQuery, isLoadingAuth, shouldHaveFree, shouldUseLegacyFlow]);

  React.useEffect(() => {
    if (USE_TIMESTAMP_POLLING) return;
    if (!shouldUseLegacyFlow) return; // realtime push replaces count-driven refetch

    if (isLoadingAuth) {
      return;
    }

    const hasChanged = paidFlowCount !== null && paidFlowCount !== prevPaidFlowCount.current;

    if (shouldHavePaid && hasChanged) {
      if (refetchPaid) {
        refetchPaid({ tradeSides: paidTradeSides });
      } else {
        getOptionFlowQuery({ variables: { tradeSides: paidTradeSides } });
      }
    }

    if (hasChanged) {
      prevPaidFlowCount.current = paidFlowCount;
    }
  }, [
    getOptionFlowQuery,
    isLoadingAuth,
    paidFlowCount,
    paidTradeSides,
    refetchPaid,
    shouldHavePaid,
    shouldUseLegacyFlow,
  ]);

  React.useEffect(() => {
    if (USE_TIMESTAMP_POLLING) return;
    if (!shouldUseLegacyFlow) return; // realtime push replaces count-driven refetch

    if (isLoadingAuth) {
      return;
    }

    const hasChanged = freeFlowCount !== null && freeFlowCount !== prevFreeFlowCount.current;

    if (shouldHaveFree && hasChanged) {
      if (refetchFree) {
        refetchFree();
      } else {
        getFreeOptionFlowQuery();
      }
    }

    if (hasChanged) {
      prevFreeFlowCount.current = freeFlowCount;
    }
  }, [
    getFreeOptionFlowQuery,
    isLoadingAuth,
    freeFlowCount,
    refetchFree,
    shouldHaveFree,
    shouldUseLegacyFlow,
  ]);

  // Control timestamp-based polling with startPolling/stopPolling (feature flagged)
  React.useEffect(() => {
    if (!USE_TIMESTAMP_POLLING) return;
    if (!shouldUseLegacyFlow) return;

    if (shouldHavePaid) {
      startPaidTimestampPolling(POLL_INTERVAL);
    } else {
      stopPaidTimestampPolling();
    }

    return () => stopPaidTimestampPolling();
  }, [shouldHavePaid, shouldUseLegacyFlow, startPaidTimestampPolling, stopPaidTimestampPolling]);

  // Control free flow polling
  React.useEffect(() => {
    if (!USE_TIMESTAMP_POLLING) return;
    if (!shouldUseLegacyFlow) return;

    if (shouldHaveFree) {
      startFreeTimestampPolling(POLL_INTERVAL);
    } else {
      stopFreeTimestampPolling();
    }

    return () => stopFreeTimestampPolling();
  }, [shouldHaveFree, shouldUseLegacyFlow, startFreeTimestampPolling, stopFreeTimestampPolling]);

  // New timestamp-based refetch (feature flagged)
  React.useEffect(() => {
    if (!USE_TIMESTAMP_POLLING) return;
    if (!shouldUseLegacyFlow) return;
    if (isLoadingAuth) return;

    if (shouldHavePaid && paidLatestTimestampData?.option_flow?.[0]?.createdAt) {
      const newTimestamp = paidLatestTimestampData.option_flow[0].createdAt;
      if (newTimestamp !== lastSeenPaidTimestamp) {
        setLastSeenPaidTimestamp(newTimestamp);
        if (refetchPaid) {
          refetchPaid({ tradeSides: paidTradeSides });
        } else {
          getOptionFlowQuery({ variables: { tradeSides: paidTradeSides } });
        }
      }
    }
  }, [
    getOptionFlowQuery,
    isLoadingAuth,
    shouldHavePaid,
    paidLatestTimestampData,
    lastSeenPaidTimestamp,
    paidTradeSides,
    refetchPaid,
    shouldUseLegacyFlow,
  ]);

  React.useEffect(() => {
    if (!USE_TIMESTAMP_POLLING) return;
    if (!shouldUseLegacyFlow) return;
    if (isLoadingAuth) return;

    if (shouldHaveFree && freeLatestTimestampData?.free_option_flow?.[0]?.createdAt) {
      const newTimestamp = freeLatestTimestampData.free_option_flow[0].createdAt;
      if (newTimestamp !== lastSeenFreeTimestamp) {
        setLastSeenFreeTimestamp(newTimestamp);
        if (refetchFree) {
          refetchFree();
        } else {
          getFreeOptionFlowQuery();
        }
      }
    }
  }, [
    isLoadingAuth,
    shouldHaveFree,
    shouldUseLegacyFlow,
    freeLatestTimestampData,
    lastSeenFreeTimestamp,
    refetchFree,
  ]);

  React.useEffect(() => {
    if (viewer.viewer?.uid) {
      getUserQuery({
        variables: {
          id: viewer.viewer.uid,
        },
      });
    }
  }, [viewer.viewer?.uid]);

  const handleFlowSideBasisChange = React.useCallback(
    (flowSideBasis: FlowSideBasisSelection) => {
      const tradeSides = getTradeSidesForFlowSideBasis(flowSideBasis);
      const normalizedBasis = normalizeFlowSideBasisSelection(flowSideBasis);

      setPaidTradeSides((currentTradeSides) =>
        areTradeSidesEqual(currentTradeSides, tradeSides) ? currentTradeSides : tradeSides,
      );
      setPaidFlowSideBasis(normalizedBasis);

      // While realtime is live the toggle is served entirely client-side (D2 filterBySides
      // over the pushed rows) — realtime rows win the render, so a full-feed Hasura refetch
      // here is dead work whose result is never shown. Skip it; on fallback the polling path
      // re-queries with the current paidTradeSides from state (§17.11 N4).
      if (!shouldHavePaid || !calledPaid || !shouldUseLegacyFlow) {
        return;
      }

      if (refetchPaid) {
        refetchPaid({ tradeSides });
      } else {
        getOptionFlowQuery({ variables: { tradeSides } });
      }
    },
    [calledPaid, getOptionFlowQuery, refetchPaid, shouldHavePaid, shouldUseLegacyFlow],
  );

  // Fetches the previewed side's rows into the separate preview feed for the
  // command center count. Does not touch the committed table feed.
  const handleFlowSidePreviewRequest = React.useCallback(
    (tradeSides: string[]) => {
      if (!shouldHavePaid) {
        return;
      }

      getPreviewOptionFlowQuery({ variables: { tradeSides } });
    },
    [getPreviewOptionFlowQuery, shouldHavePaid],
  );

  if (!injectedPageData && isLoadingAuth) {
    return <Placeholder />;
  }

  // if (!injectedPageData && viewer.status === AuthStatus.User && !hasDashboardAccess(viewer)) {
  //   router.push(PRICING_PAGE);
  //   return <Placeholder />;
  // }

  if (
    !injectedPageData &&
    viewer.status === AuthStatus.User &&
    hasDashboardAccess(viewer) &&
    !hasCompletedOpraAgreement(viewer)
  ) {
    router.push(ELECTRONIC_AGREEMENT_PAGE);
    return <Placeholder />;
  }

  return (
    <>
      <Head
        title={TITLE}
        description={DESCRIPTION}
        useCurrentUrl
        ogImage="https://www.insiderfinance.io/images/hero-dashboard.png"
      />
      {/* <div className="fixed left-0 top-0 z-50 w-full">
        <TopBanner showCloseIcon shouldShowOnScroll={false} />
      </div> */}
      <FlowPageContainer
        viewer={viewer}
        title="Flow"
        Icon={PageIcons.OptionFlow}
        showBaselineToggle={showBaselineToggle}
        canUseBaseline={canUseBaseline}
        showBaseline={showBaseline}
        onToggleBaseline={setShowBaseline}
        trades={
          realtimeAdapted
            ? (realtimeAdapted.flow as unknown as GetOptionFlowQuery['option_flow'])
            : shouldUseLegacyFlow
            ? paidOptionFlowData?.option_flow || freeOptionFlowData?.free_option_flow
            : undefined
        }
        dailyVolume={
          realtimeAdapted
            ? (realtimeAdapted.dailyVolume as unknown as GetOptionFlowQuery['daily_volume'])
            : shouldUseLegacyFlow
            ? paidOptionFlowData?.daily_volume || freeOptionFlowData?.daily_volume
            : undefined
        }
        // Realtime: `trades` are raw FlowRows; join ticker fields once in FlowPageContainer's
        // derive pass instead of pre-building a second full adapted array here.
        tickerInfo={usingRealtime ? realtimeTickers : undefined}
        loading={
          !shouldUseLegacyFlow
            ? !usingRealtime
            : (shouldHavePaid && isLoadingPaid) || (shouldHaveFree && isLoadingFree)
        }
        called={!shouldUseLegacyFlow ? true : isInitialFlowCalled}
        userDetails={userData?.users_by_pk}
        OptionFlowTable={OptionFlowTable}
        shouldShowDatePicker={false}
        injectedPageData={injectedPageData}
        pageHref={OPTION_FLOW_PAGE}
        isShowFlowDescription={
          // SEO-only text — never render it in the native app (no SEO value in a
          // WebView, and it becomes visible if anything makes the page scrollable)
          !injectedPageData && process.env.TARGET_PLATFORM !== 'MOBILE' && !getIsNativePlatform()
        }
        totalFlowCount={totalFlowCount}
        delayedFlowWait={delayedFlowWait}
        onFlowSideBasisChange={handleFlowSideBasisChange}
        previewTrades={previewData?.option_flow}
        isPreviewLoading={isPreviewLoading}
        onFlowSidePreviewRequest={handleFlowSidePreviewRequest}
      />

      {!injectedPageData && <FloatingBox />}
    </>
  );
}
